
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { WhatsappCampaign } from '@core/models/whatsapp-campaign.model';
import { Pagination } from '@core/models/pagination.model';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import * as moment from 'moment-timezone';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, exhaustMap, map, tap } from 'rxjs/operators';
import { WhatsappCampaignDataService } from './service/whatsapp-campaign-data.service';
import { WhatsappCampaignEntityService } from './service/whatsapp-campaign-entity.service';
import { WhatsappCampaignStatus } from '@core/enums/whatsapp-campaign-status.enum';
import { AppPermissionService } from '@core/services/app-permission.service';
import { MatDialog } from "@angular/material/dialog";
import Swal from 'sweetalert2';
import { MemberInformationDialogComponent } from '../../general/members/dialogs/member-information/member-information.component';
import { MemberDataService } from './../../general/members/services/member-data.service';
import { WhatsappCampaignSettingDialogComponent } from './dialogs/whatsapp-campaign-setting/whatsapp-campaign-setting.component';

@Component({
  selector: 'kt-whatsapp-campaign',
  templateUrl: './whatsapp-campaign.component.html',
  styleUrls: ['./whatsapp-campaign.component.scss']
})
export class WhatsappCampaignComponent implements OnInit, OnDestroy {

  whatsappCampaign$: Observable<WhatsappCampaign[]>;
  form: FormGroup;
  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  dropdown = {
    perPage: this.dropdownHttpService.perPage,
    statuses: [
      {
        name: 'Pending', value: 0, checked: true
      },
      {
        name: 'Completed', value: 1, checked: true
      },
      {
        name: 'Failed', value: 2, checked: true
      }
    ],
    source: this.dropdownHttpService.whatsappCampaignSource
  };
  ranges: any;
  messages$ = this.whatsappCampaignDataService.messages$;
  checkValidation = false;
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  importBtnLoading = false;
  settingBtnLoading = false;
  dataLength: number;
  searchStatus = [0, 1, 2];
  status = WhatsappCampaignStatus;
  defaultStart = this.transactionHttpService.getToday().from;
  defaultEnd = this.transactionHttpService.getToday().to;

  private subscription = new Subscription();
  private subscriptions = new Subscription();
  private datePickerSubscription = new Subscription();

  dateTimeFilterType = [
    {
      key: 'created_at',
      label: 'Created Date'
    },
    {
      key: 'updated_at',
      label: 'Updated Date'
    }
  ];

  default_sort_by = 'created_at';
  default_sort_order = 'desc';

  sortingConfig = {
    'campaign_code': 'desc',
    'username': 'desc',
    'mobile': 'desc',
    'status': 'desc',
    'created_at': 'desc',
    'updated_at': 'desc',
  };

  sortingSelection = {
    'sort_by': this.default_sort_by,
    'sort_order': this.default_sort_order,
  };

  // permissions
  canViewMemberDialog: boolean;
  canEditSetting: boolean;

  constructor(
    private loadingBar: LoadingBarService,
    private whatsappCampaignEntityService: WhatsappCampaignEntityService,
    private whatsappCampaignDataService: WhatsappCampaignDataService,
    private transactionHttpService: TransactionHttpService,
    private dropdownHttpService: DropdownHttpService,
    private appPermissionService: AppPermissionService,
    public dialog: MatDialog,
    private memberDataService: MemberDataService,
  ) { }

  ngOnInit() {
    this.formInit();
    this.pagination = this.whatsappCampaignDataService.pagination;
    this.ranges = this.transactionHttpService.ranges;

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canViewMemberDialog = appPermissions.whatsapp_campaign_view_member_dialog;
      this.canEditSetting = appPermissions.whatsapp_campaign_edit_setting;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
    this.datePickerSubscription.unsubscribe();
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.whatsappCampaign$ = this.whatsappCampaignEntityService.getWithQuery(`?page=${page}&perPage=${pageSize}${params}&${this.generateSortingParam()}`).pipe(
      tap(res => {
        this.loading = false;
        this.dataLength = res.length;
        this.pagination = this.whatsappCampaignDataService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  onPerPage(size: Event) {
    this.page = 1;
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onOpenSettingDialog() {
    this.settingBtnLoading = true;

    this.subscription = this.whatsappCampaignDataService.getSetting().pipe(
      tap((res) => {
        this.settingBtnLoading = false;
        if (res) {
          this.openDialogBy(WhatsappCampaignSettingDialogComponent, { mode: 'blast-setting', setting: res });
        }
      }),
      catchError((error) => {
        this.settingBtnLoading = false;
        throw error;
      })
    ).subscribe();
  }

  onClearDate() {
    if (this.form.value.start_datetime !== null) {
      this.form.patchValue({ 
        start_datetime: this.defaultStart,
        end_datetime: this.defaultEnd,
        defaultDate: {
          startDate: this.defaultStart,
          endDate: this.defaultEnd
        }
      });
    }
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_datetime: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_datetime: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  onClear() {
    this.clearBtnLoading = true;
    this.searchStatus = [0, 1, 2];
    this.form.patchValue({
      campaign_code: null,
      username: null,
      mobile: null,
      source: 'all',
      status: this.searchStatus,
      start_datetime: this.defaultStart,
      end_datetime: this.defaultEnd,
      defaultDate: {
        startDate: this.defaultStart,
        endDate: this.defaultEnd
      },
      date_type: this.dateTimeFilterType[0].key
    });
    this.dropdown.statuses.forEach((child) => {  
      child.checked = true;   
    });
    this.onSubmit(true);
  }

  onSubmit(clearSearch?: boolean) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.searchBtnLoading = clearSearch ? false : true;
    this.loading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        if (data['start_datetime']) {
          data['start_datetime'] = moment(data['start_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_datetime'] = moment(data['end_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        const statusParams = this.generateStatusParams();
        const paramsRef = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.params = paramsRef.replace(statusParams.oldStatusParams, statusParams.newStatusParams);
        const parameters = this.params ? `&${this.params}` : '';
        this.loadingBar.start();
        return this.whatsappCampaign$ = this.whatsappCampaignEntityService.getWithQuery(`?perPage=${this.pageSize}${parameters}&${this.generateSortingParam()}`).pipe(
          tap(res => {
            this.page = 1
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.dataLength = res.length;
            this.pagination = this.whatsappCampaignDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  private formInit() {
    this.form = new FormGroup({
      campaign_code: new FormControl(null),
      username: new FormControl(null),
      mobile: new FormControl(null),
      source: new FormControl('all'),
      status: new FormControl(this.searchStatus),
      start_datetime: new FormControl(this.defaultStart),
      end_datetime: new FormControl(this.defaultEnd),
      defaultDate: new FormControl({
        startDate: this.defaultStart,
        endDate: this.defaultEnd
      }), // Do not remove: For Clearing The Range
      date_type: new FormControl(this.dateTimeFilterType[0].key)
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== 'all' && key !== 'defaultDate') {
        fields[key] = formData[key];
      };
    });
    return fields;
  }

  onSearchStatus(event: any, id: number, ind: number) {
    const position = this.searchStatus.indexOf(id);
    if (event.target.checked) {
      this.searchStatus.push(id);
      this.dropdown.statuses[ind].checked = true;
    } else {
      this.searchStatus.splice(position, 1);
      this.dropdown.statuses[ind].checked = false;
    }
    this.form.patchValue({ status: this.searchStatus });
  }

  private generateStatusParams() {
    let params = {
      newStatusParams: Object.keys(this.searchStatus).map(key => `status[${key}]=${this.searchStatus[key]}`).join('&'),
      oldStatusParams: 'status=' + Object.keys(this.searchStatus).map(key => this.searchStatus[key]).join(','),
    }
    return params;
  }

  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }

  onSortColumn(property: string) {
    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }
      }
    }
    this.sortingSelection.sort_by = property;
    this.sortingSelection.sort_order = this.sortingConfig[property];
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  private openDialogBy(componentRef: any, data?: { mode?: any, member?: any, setting?: any}) {
    this.dialog.open(componentRef, {
      width: data.mode === 'member-information' ? '1500px' : data.mode === 'blast-setting' ? '700px' : '800px',
      height: data.mode === 'member-information' ? '80vh' : 'auto',
      data: {
        mode: data.mode,
        member: data.member,
        setting: data.setting
      },
    });
  }

  onOpenDialog(type: string, row?: any ) {  
    if (type === 'member-information') {
      if (!this.canViewMemberDialog) {
        Swal.fire('Permission Error', 'You do not have permission to view member information', 'error');
        return;
      }
    
      const member = this.memberDataService.getById(row, `?start_date=${moment(this.transactionHttpService.getYesterday().from).utc().format('YYYY-MM-DD HH:mm:ss')}&end_date=${moment(this.transactionHttpService.getLast24Hours().to).utc().format('YYYY-MM-DD HH:mm:ss')}&additional_info=1`);
      this.subscription = member.pipe(
        tap((res) => {
          if (res) {
            this.openDialogBy(MemberInformationDialogComponent, { mode: 'member-information', member: res });
          }
        })
      ).subscribe();
    }
  }
}
